import { modal } from '@codeleap/modals'
import { View, Text, PoweredBy, ActionIcon, Button, ActivityIndicator } from '@/components'
import { theme } from '@/config'
import { ProfileBanner } from '../shared/ProfileBanner'
import { ReportUserModal } from './ReportModal'
import { ChatUser, useChatUserImage } from '@packages/chat'
import { useIsTablet } from '@/hooks'
import { APIClient } from '@/services'

const Info = ({ title, text }) => (
  <View style={['column', 'alignStart', 'justifyStart', 'gap:0.2', 'px:2']}>
    <Text text={title} style={['medium', 'c2', { color: theme.colors.textCaption }]} />
    <Text text={text} style={['medium', 'p3', { color: theme.colors.textHeadingHighlighted }]} />
  </View>
)

export type UserDetailsModalProps = {
  user: ChatUser
}

export const UserDetailsModal = modal<UserDetailsModalProps>({ id: 'user-details-modal' }).content((props) => {
  const { toggle } = props

  const id = props?.user?.id

  const { companyName, companyDepartment, companyLocation } = APIClient.Session.useCompanyBranding()

  const { data: user, query } = APIClient.Chat.usersManager.useRetrieve({ id, queryOptions: { enabled: !!id, select: (data) => data } })
  APIClient.Profiles.profilesManager.useItem({ id, queryOptions: { enabled: !!id, select: (data) => data } })

  const userImage = useChatUserImage(user?.id, user?.image)

  const isTablet = useIsTablet()

  UserDetailsModal.useProps({
    showClose: false,
    style: [isTablet ? 'fullscreen' : 'box:responsiveness', 'centered', { box: ['padding:2'] }],
  })

  const infos = [
    {
      label: 'Organisation',
      content: companyName,
    },
    {
      label: 'Department',
      content: companyDepartment,
    },
    {
      label: 'Location',
      content: companyLocation,
    },
  ].filter(info => info.content && info.content.toLowerCase() !== 'none')

  if (query?.isFetching) return <View style={['fullWidth', 'center']}><ActivityIndicator /></View>

  return (
    <View style={['column', 'gap:2']}>
      <View style={['fullWidth', 'alignCenter', 'justifySpaceBetween']}>
        <ActionIcon
          icon='chevron-left'
          style={['normalize', 'iconSize:3', 'color:textHeadingRegular']}
          onPress={toggle}
        />

        <Text text='Information' style={['semiBold', 'p2', { color: theme.colors.textHeadingRegular }]} />

        <View />
      </View>

      <View style={['column', 'alignStart', 'justifyStart', 'gap:2', 'pb:6']}>
        <ProfileBanner name={user?.name} avatar={userImage} description={user?.job_title} />

        <View style={['column', 'gap:2', 'mt:1']}>
          {infos.map(info => (
            <Info key={info.label} title={info.label} text={info.content} />
          ))}
        </View>

        <Button
          debugName='profile:option'
          text='Report'
          icon='flag-3-line'
          style={['row', 'row:negative', 'padding:2']}
          onPress={() => ReportUserModal.request({ profileId: user?.id })}
        />
      </View>

      <PoweredBy />
    </View>
  )
})
